/**
 * Перемотка −15 / +15 в видеоплеере урока — как в YouTube (13.09.2026).
 *
 * 🔴 ЗАЧЕМ. Чтобы переслушать фразу, ученик тянул ползунок, а на телефоне это
 * промах на полминуты в любую сторону. Кнопки «−15» / «+15» в панели делают то же
 * одним нажатием.
 *
 * Логика вынесена из `components/media/YouTubePlayer.tsx`, потому что проверки
 * собирают только `*.test.ts` — правило внутри `.tsx` не проверялось бы вовсе
 * (тот же приём, что у `lib/playerPanel.ts`).
 *
 * ⚠️ ДВОЙНОГО КАСАНИЯ ПО КРАЯМ КАДРА НЕТ — решение владельца 13.09.2026: достаточно
 * кнопок. Оно было сделано и убрано: заодно ушла задержка в 300 мс, с которой на
 * телефоне срабатывало обычное касание сбоку кадра.
 */

/** Шаг перемотки, секунд. */
export const SEEK_STEP_S = 15;

/**
 * Новое время после шага.
 *
 * ⚠️ ДЛИНА БЫВАЕТ НУЛЁМ. До первых метаданных YouTube отдаёт длину 0, и
 * ограничение «не дальше конца» отправило бы ролик в начало. Неизвестная длина —
 * ограничиваем только началом.
 */
export function seekBy(current: number, delta: number, duration: number): number {
  const from = Number.isFinite(current) && current > 0 ? current : 0;
  const next = Math.max(0, from + delta);
  return Number.isFinite(duration) && duration > 0 ? Math.min(next, duration) : next;
}

/** Сколько после шага доверять его цели, а не ответу плеера, миллисекунд. */
export const SEEK_SETTLE_MS = 1000;

/**
 * От какой точки считать шаг.
 *
 * 🔴 НАЙДЕНО ЖИВОЙ ПРОВЕРКОЙ НА СТЕНДЕ (13.09.2026). Первая версия всегда брала время у
 * плеера YouTube. У незапущенного или стоящего ролика он отвечает с опозданием или нулём:
 * «+15», «+15» дали 0:15 и 0:30, а «−15» — 0:00 вместо 0:15.
 *
 * ⚠️ «ВСЕГДА СОСТОЯНИЕ» ТОЖЕ НЕВЕРНО. Пока ролик играет, время в состоянии обновляется
 * опросом раз в 250 мс, и три быстрых касания считались бы от одной точки — «−45»
 * превращалось бы в «−15». Отсюда порядок:
 *
 * 1. недавний шаг (`SEEK_SETTLE_MS`) — от его цели: плеер ещё не успел её принять;
 * 2. ролик играет и плеер ответил числом — от времени плеера;
 * 3. иначе — от состояния: у стоящего ролика опрос не идёт, и его двигаем только мы сами.
 */
export function seekFrom(i: {
  playing: boolean;
  /** Что ответил плеер; `null` — не ответил. */
  playerTime: number | null;
  stateTime: number;
  lastTarget: { time: number; at: number } | null;
  now: number;
}): number {
  if (i.lastTarget) {
    const age = i.now - i.lastTarget.at;
    if (age >= 0 && age < SEEK_SETTLE_MS) return i.lastTarget.time;
  }
  if (i.playing && typeof i.playerTime === 'number' && Number.isFinite(i.playerTime)) return i.playerTime;
  return i.stateTime;
}

